"use client";

import { createContext, useContext, useId, useState, type ReactNode } from "react";
import styles from "./Accordion.module.css";

/*
  Replaces the Webflow `w-dropdown` widget, which the export used for every
  accordion on the site: the FAQ list and the mobile "Why Quantstamp" mirror
  (where opening one closes its siblings) and the team bios (where each opens
  independently).

  Those two behaviours are the reason this is a pair of components rather than
  one. A bare <Accordion> owns its own open state; wrapping a set of them in
  <AccordionGroup> hands that state to the parent so only one can be open at a
  time. The old code expressed the same split by calling `.closest()` on two
  hard-coded class names, which meant the behaviour lived in a global effect
  instead of at the call site.
*/

type GroupContext = {
  openId: string | null;
  setOpenId: (id: string | null) => void;
};

const AccordionGroupContext = createContext<GroupContext | null>(null);

export function AccordionGroup({ children, className }: { children: ReactNode; className?: string }) {
  const [openId, setOpenId] = useState<string | null>(null);
  return (
    <AccordionGroupContext.Provider value={{ openId, setOpenId }}>
      <div className={className}>{children}</div>
    </AccordionGroupContext.Provider>
  );
}

export function Accordion({
  summary,
  children,
  className,
  triggerClassName,
  panelClassName,
  defaultOpen = false,
}: {
  summary: ReactNode;
  children: ReactNode;
  className?: string;
  triggerClassName?: string;
  panelClassName?: string;
  /* Ignored inside an <AccordionGroup>, which starts with everything closed. */
  defaultOpen?: boolean;
}) {
  const id = useId();
  const group = useContext(AccordionGroupContext);
  const [selfOpen, setSelfOpen] = useState(defaultOpen);
  const open = group ? group.openId === id : selfOpen;

  function toggle() {
    if (group) group.setOpenId(open ? null : id);
    else setSelfOpen(!open);
  }

  return (
    <div className={[styles.root, className].filter(Boolean).join(" ")} data-open={open || undefined}>
      <button
        type="button"
        id={`${id}-trigger`}
        className={[styles.trigger, triggerClassName].filter(Boolean).join(" ")}
        aria-expanded={open}
        aria-controls={`${id}-panel`}
        onClick={toggle}
      >
        <span className={styles.summary}>{summary}</span>
        <span className={styles.chevron} aria-hidden="true" />
      </button>
      {/*
        The 0fr→1fr grid row is what animates the height. `visibility` (not
        `display`) does the hiding so the transition has something to run on,
        and it still takes the panel out of the tab order when closed.
      */}
      <div className={styles.panel} id={`${id}-panel`} role="region" aria-labelledby={`${id}-trigger`}>
        <div className={[styles.panelInner, panelClassName].filter(Boolean).join(" ")}>{children}</div>
      </div>
    </div>
  );
}
